import React, { useMemo } from "react";
import {
  useCheckoutStateSelector as useSelector,
  PriceBreakdownDropdown,
  PriceBreakdownContent,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { ILineItemProps } from "@hopper-b2b/types";

import {
  getFlightTotalInPrices,
  getCurrencyFromPricingInfo,
  getIsPriceBreakdownLoading,
  getTripPriceLineItems,
  getSummaryLineItems,
  getShopPricingInfo,
  getTripCategory,
  getConvenienceFeeLineItems,
} from "../selectors";

export const useConvenienceFeeLineItems = (
  lineItems: ILineItemProps[]
): ILineItemProps[] => {
  const convenienceFeeLineItems = useSelector(getConvenienceFeeLineItems);
  return useMemo(() => {
    if (convenienceFeeLineItems.length === 0 || lineItems.length === 0)
      return lineItems;
    const [fee] = convenienceFeeLineItems;
    const paxCount = lineItems.length;
    const perPaxValue = Math.floor((fee.price.value / paxCount) * 100) / 100;
    const remainder =
      Math.round((fee.price.value - perPaxValue * paxCount) * 100) / 100;
    return lineItems.map((item, index) => ({
      ...item,
      products: {
        ...item.products,
        [fee.key]: {
          ...fee.price,
          value: index === paxCount - 1 ? perPaxValue + remainder : perPaxValue,
        },
      },
    }));
  }, [lineItems, convenienceFeeLineItems]);
};

export const ConnectedPriceBreakdownDropdown = () => {
  const { t } = useI18nContext();

  const flightTotalInPrices = useSelector(getFlightTotalInPrices);
  const tripCurrency = useSelector(getCurrencyFromPricingInfo);
  const isPriceBreakdownLoading = useSelector(getIsPriceBreakdownLoading);
  const tripPriceLineItems = useSelector(getTripPriceLineItems);
  const tripPricingSummaryItems = useSelector(getSummaryLineItems);
  const shopPricingInfo = useSelector(getShopPricingInfo);
  const tripCategory = useSelector(getTripCategory);
  const lineItemsWithFee = useConvenienceFeeLineItems(tripPriceLineItems);

  return (
    <PriceBreakdownDropdown
      flightTotal={flightTotalInPrices}
      currency={tripCurrency}
      isLoading={isPriceBreakdownLoading}
      lineItems={lineItemsWithFee}
      summaryLineItems={tripPricingSummaryItems}
      header={t("checkoutBreakdown")}
      // Hardcoded fintech values
      isPriceFreezeExerciseEnabled={false}
      priceDropProtection={null}
      initialPassengerPricing={shopPricingInfo?.fare?.[0]?.pricing}
      tripCategory={tripCategory}
    />
  );
};

export const ConnectedPriceBreakdownContent = () => {
  const { t } = useI18nContext();
  const shopPricingInfo = useSelector(getShopPricingInfo);
  const tripCurrency = useSelector(getCurrencyFromPricingInfo);
  const isPriceBreakdownLoading = useSelector(getIsPriceBreakdownLoading);
  const tripPriceLineItems = useSelector(getTripPriceLineItems);
  const tripPricingSummaryItems = useSelector(getSummaryLineItems);
  const tripCategory = useSelector(getTripCategory);
  const lineItemsWithFee = useConvenienceFeeLineItems(tripPriceLineItems);
  return (
    <PriceBreakdownContent
      isLoading={isPriceBreakdownLoading}
      initialPassengerPricing={shopPricingInfo?.fare?.[0]?.pricing}
      currency={tripCurrency}
      lineItems={lineItemsWithFee}
      summaryLineItems={tripPricingSummaryItems}
      header={t("checkoutBreakdown")}
      tripCategory={tripCategory}
      customClassName={"checkout-price-breakdown-content"}
    />
  );
};
